import type { FC } from 'react';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';

import { DRAG_ITEM_TYPES } from 'src/constants/dragItem';
import type { ReportOrderStatus } from 'src/constants/report';
import {
  API_RESULT_LIMITS,
  SEARCH_FORM_SETTINGS,
} from 'src/constants/settings';
import type { TemplateOrReportItem } from 'src/types/workspace';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { useConfirm } from 'src/components/app/context/ConfirmContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import ItemActions from 'src/components/common/CustomItemList/components/ItemActions';
import ListWithSearch from 'src/components/common/ListWithSearch';
import ReportShareModal from 'src/components/common/ReportShareModal';
import { ShoppingCartContext } from 'src/components/report/ShoppingCartContext';
import useReportConfigMaps from 'src/components/reportConfig/hooks/useReportConfigMaps';
import { useWorkspaceContext } from 'src/components/workspace/WorkspaceContext';

import CustomItem from './components/CustomItem';
import ItemInfoModal from './components/ItemInfoModal';

export interface CustomItemListProps {
  customItemType: 'datalines' | 'reports' | 'sharedReports';
  infiniteScrolling?: boolean;
}

/* Previously imported as "CustomReportList". Since the customItemType is not necessarily a report,
however (see CustomItemListProps), this name is less misleading.
 */
const CustomItemList: FC<CustomItemListProps> = ({
  customItemType,
  infiniteScrolling = false,
}) => {
  const { t } = useTranslation(['report', 'common']);
  const {
    customReports,
    customReportsPaginationStatus,
    datalineTemplatesPaginationStatus,
    datalineTemplates,
    deleteDatalineTemplate,
    deleteReport,
    getDatalineTemplates,
    getCustomReports,
  } = useWorkspaceContext();
  const { addItem } = useContext(ShoppingCartContext);

  const { user } = useUserContext();

  const [initialListItems, setInitialListItems] = useState<
    TemplateOrReportItem[]
  >([]);
  const [listItems, setListItems] = useState<TemplateOrReportItem[]>([]);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TemplateOrReportItem | null>(
    null,
  );
  const [inputTimeout, setInputTimeout] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isFetchingNextItems, setIsFetchingNextItems] = useState(false);
  const [paginationStatus, setPaginationStatus] = useState<any>(null);

  const {
    reportNamesPerReportType,
    abbreviationPerFileType,
    reportConfigsLoading,
  } = useReportConfigMaps();
  const confirm = useConfirm();

  // If they aren't reports, then they are datalines.
  const typeIsReport =
    customItemType === 'reports' || customItemType === 'sharedReports';

  // sharedReports = getReport reports
  const sharedOnly = customItemType === 'sharedReports';

  const itemType = typeIsReport ? 'report' : 'dataLine';

  const showActionButtons =
    customItemType === 'reports' || customItemType === 'datalines';

  // Nested ternary expression in order to set query search route.
  // In getReport only shared reports can be seen and searched.
  const searchEndpoint =
    typeIsReport && sharedOnly
      ? apiPaths.GET_CUSTOM_REPORTS_SHARED_QUERY_SEARCH
      : typeIsReport
        ? apiPaths.GET_CUSTOM_REPORTS_QUERY_SEARCH
        : apiPaths.GET_TEMPLATES_QUERY_SEARCH;

  const searchInputPlaceholder = typeIsReport
    ? t('sidebar.searchReport')
    : t('sidebar.searchDataLine');

  useEffect(() => {
    // customReports may be null but there is no warning because the context is not in ts
    if (customReports === null) return;

    if (typeIsReport) {
      setListItems(customReports);
      setInitialListItems(customReports);
      setIsLoading(false);
    } else if (datalineTemplates) {
      setListItems(datalineTemplates);
      setInitialListItems(datalineTemplates);
      setIsLoading(false);
    }
  }, [customReports, datalineTemplates, typeIsReport]);

  useEffect(() => {
    setPaginationStatus(
      typeIsReport
        ? customReportsPaginationStatus
        : datalineTemplatesPaginationStatus,
    );
  }, [
    customReportsPaginationStatus,
    datalineTemplatesPaginationStatus,
    typeIsReport,
  ]);

  const openInfoModal = (item: TemplateOrReportItem) => {
    setSelectedItem(item);
    setShowInfoModal(true);
  };

  const onInfoModalClose = () => {
    setShowInfoModal(false);
    setSelectedItem(null);
  };

  const openShareModal = (item: TemplateOrReportItem) => {
    setSelectedItem(item);
    setShowShareModal(true);
  };

  const onShareModalClose = (didShare = false) => {
    setShowShareModal(false);
    setSelectedItem(null);

    // if we shared a report or template, we should update the list to reflect the changes
    if (didShare) {
      setIsLoading(true);

      if (typeIsReport) getCustomReports({ reset: true });
      else getDatalineTemplates(true);
    }
  };

  const handleDelete = async (item: TemplateOrReportItem) => {
    const deleteKey = `report:deleteItem_${itemType}`;

    await confirm({
      description: t('deleteWarning', {
        itemType: t(itemType),
        context: itemType,
      }),
      primaryText: deleteKey,
      title: t('reportDeleteHeadline', { name: item.name }),
    })
      .then(() => {
        if (typeIsReport) deleteReport(item.id);
        else deleteDatalineTemplate(item.id);
      })
      .catch(() => undefined);
  };

  const handleSearch = (queryString: string | null) => {
    const fetchSearchResults = () => {
      const requestEndpoint = queryString
        ? `${searchEndpoint}${queryString}`
        : searchEndpoint;
      const params = { limit: API_RESULT_LIMITS.SEARCH };

      return HttpService.get(requestEndpoint, params, true).then(
        (response: any) => {
          const {
            data: { results: searchResultItems },
          } = response;
          setListItems(searchResultItems);
          setIsLoading(false);
        },
      );
    };

    // Clear a possible input delay timeout on every input update.
    clearTimeout(inputTimeout);

    if (queryString) {
      setIsLoading(true);
      setIsSearching(true);
      setInputTimeout(
        setTimeout(fetchSearchResults, SEARCH_FORM_SETTINGS.DEBOUNCE_TIME),
      );
    } else {
      setListItems(initialListItems);
      setIsLoading(false);
      setIsSearching(false);
    }
  };

  const loadMoreItems = () => {
    if (!paginationStatus?.hasNextPage || isSearching || isFetchingNextItems)
      return;

    setIsFetchingNextItems(true);
    if (typeIsReport) {
      getCustomReports({
        reset: false,
      }).then(() => {
        setIsFetchingNextItems(false);
      });
    } else {
      getDatalineTemplates().then(() => {
        setIsFetchingNextItems(false);
      });
    }
  };

  // Global Champions get access to all reports in their orgTree
  // They should, however, not be able to share, star or delete these reports
  const canRenderContextActions = useCallback(
    (item: TemplateOrReportItem) => {
      if (!typeIsReport) return true;

      if (item.created_by) {
        if (!!user && item.created_by.id === user.id) {
          return true;
        }

        if (item.shared_by) {
          return true;
        }
      }

      return false;
    },
    [typeIsReport, user],
  );

  return (
    <>
      <ListWithSearch
        handleSearch={handleSearch}
        isLoading={isLoading}
        isSearching={isSearching}
        searchInputPlaceholder={searchInputPlaceholder}
      >
        <>
          {listItems && listItems.length
            ? listItems.map((item: TemplateOrReportItem) => {
                const itemIsReport = 'report_type' in item;
                const { id, name, is_favourite: isFavourite } = item;
                const orderStatus = itemIsReport
                  ? item.order_status
                  : undefined;
                const isGlobal = !itemIsReport
                  ? !!item.is_global_template
                  : false;
                const isLocked = 'locked' in item ? !!item.locked : false;

                return (
                  <CustomItem
                    orderStatus={orderStatus as ReportOrderStatus}
                    dragSourceType={
                      itemIsReport
                        ? DRAG_ITEM_TYPES.CUSTOM_REPORT
                        : DRAG_ITEM_TYPES.DATALINE
                    }
                    isReadOnly={isGlobal || isLocked}
                    isFavourite={!!isFavourite}
                    id={id}
                    item={item}
                    key={id}
                    title={name}
                  >
                    <ItemActions
                      item={item}
                      isGlobal={isGlobal}
                      canRenderContextActions={canRenderContextActions(item)}
                      showActionButtons={showActionButtons}
                      openShareModal={openShareModal}
                      handleDelete={handleDelete}
                      addItem={addItem}
                      openInfoModal={openInfoModal}
                      showShareButton={
                        itemIsReport &&
                        customItemType === 'sharedReports' &&
                        !!item.file
                      }
                    />
                  </CustomItem>
                );
              })
            : isSearching && (
                <Typography sx={{ mt: 0 }}>
                  {t('nothingFound', {
                    itemType: t(itemType, { count: 2 }),
                  })}
                </Typography>
              )}
        </>
      </ListWithSearch>
      {infiniteScrolling &&
        !isSearching &&
        !isLoading &&
        !isFetchingNextItems &&
        paginationStatus.hasNextPage && (
          <Button variant="cta" onClick={loadMoreItems} fullWidth>
            {t('loadMore', { itemType: t(itemType, { count: 2 }) })}
          </Button>
        )}
      {isFetchingNextItems && (
        <Stack direction="row" sx={{ justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Stack>
      )}
      {showInfoModal && selectedItem && (
        <ItemInfoModal
          headline={t('sidebar.information', { itemType: t(itemType) })}
          item={selectedItem}
          isOpen={showInfoModal}
          onClose={onInfoModalClose}
          showFavouriteButton={canRenderContextActions(selectedItem)}
          reportNamesPerReportType={reportNamesPerReportType}
          abbreviationPerFileType={abbreviationPerFileType}
          loadingReportConfigData={reportConfigsLoading}
        />
      )}
      {showShareModal && selectedItem && (
        <ReportShareModal
          headline={t('share', {
            itemType: t(itemType),
            name: selectedItem.name,
          })}
          reportOrDataline={selectedItem}
          isReport={typeIsReport}
          onClose={onShareModalClose}
        />
      )}
    </>
  );
};

export default CustomItemList;
